import * as THREE from 'three'

interface QuadRenderSegment {
  x: number
  y: number
  width: number
  height: number
}

/**
 * QuadRender - 全屏四边形渲染器
 *
 * 用于执行全屏 Quad 渲染，通常配合自定义 ShaderMaterial 使用。
 * 对应 PlayCanvas 的 QuadRender 类。
 *
 * @remarks
 * 功能特点：
 * 1. **全屏四边形渲染**：使用 TriangleStrip 拓扑绘制 4 个顶点
 * 2. **Viewport/Scissor 控制**：支持局部渲染区域
 * 3. **材质绑定**：绑定传入的 ShaderMaterial
 *
 * Three.js 实现方式：
 * - 使用 PlaneGeometry + Mesh + OrthographicCamera
 * - 材质由外部传入（解耦设计）
 * - 通过 Scene + Camera 完成渲染
 */
export class QuadRender {
  /**
   * 渲染材质
   */
  private material: THREE.ShaderMaterial

  /**
   * 全屏四边形几何体
   */
  private geometry: THREE.PlaneGeometry

  /**
   * 全屏四边形 Mesh
   */
  private mesh: THREE.Mesh

  /**
   * 渲染场景（仅包含 mesh）
   */
  private scene: THREE.Scene

  /**
   * 正交相机（覆盖整个 NDC 空间 [-1, 1]）
   */
  private camera: THREE.OrthographicCamera

  /**
   * 分段 instanced quad 几何体（用于 WorkBuffer copy segments）
   */
  private segmentGeometry: THREE.InstancedBufferGeometry | null = null

  /**
   * 分段渲染 Mesh
   */
  private segmentMesh: THREE.Mesh | null = null

  /**
   * 分段渲染场景
   */
  private segmentScene: THREE.Scene | null = null

  /**
   * instance rect 数据：[x, y, width, height]
   */
  private segmentRects: Float32Array = new Float32Array(0)

  /**
   * instance rect 属性
   */
  private segmentRectAttribute: THREE.InstancedBufferAttribute | null = null

  /**
   * 当前分段 instance 容量
   */
  private segmentCapacity = 0

  /**
   * 创建 QuadRender
   *
   * @param material - 要使用的着色器材质
   *
   * @remarks
   * PlayCanvas 的 QuadRender 接受 Shader，这里改为接受 Material
   * 这样更符合 Three.js 的设计习惯
   */
  constructor(material: THREE.ShaderMaterial) {
    this.material = material

    // 创建全屏四边形几何体
    // PlayCanvas 使用 TriangleStrip，Three.js 使用三角形对
    // 顶点覆盖整个 NDC 空间 [-1, 1]
    this.geometry = new THREE.PlaneGeometry(2, 2)
    // 预设 boundingSphere，避免 computeBoundingSphere 触发多余警告
    this.geometry.boundingBox = new THREE.Box3(
      new THREE.Vector3(-1, -1, -1),
      new THREE.Vector3(1, 1, 1)
    )
    this.geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), Math.SQRT2)
    this.geometry.computeBoundingSphere = () => {}

    // 创建 Mesh
    this.mesh = new THREE.Mesh(this.geometry, material)
    this.mesh.frustumCulled = false // 禁用视锥剔除（全屏 quad 总是可见）

    // 创建场景
    this.scene = new THREE.Scene()
    this.scene.add(this.mesh)

    // 创建正交相机（覆盖 [-1, 1] NDC 空间）
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
  }

  /**
   * 渲染全屏四边形到当前 render target
   *
   * @param renderer - WebGL 渲染器
   *
   * @remarks
   * 全屏渲染：quad 覆盖整个 render target。
   * 局部写入由 shader 通过 uStartLine/uActiveSplats 控制（discard 越界片元），
   * WorkBuffer copy segments 使用 renderSegments() 提交局部矩形。
   */
  render(renderer: THREE.WebGLRenderer): void {
    renderer.render(this.scene, this.camera)
  }

  precompile(renderer: THREE.WebGLRenderer): Promise<void> | void {
    this.ensureSegmentGeometry(1)

    type PrecompileRenderer = THREE.WebGLRenderer & {
      compileAsync?: (
        scene: THREE.Scene,
        camera: THREE.Camera,
        targetScene?: THREE.Scene | null
      ) => Promise<unknown>
      compile?: (
        scene: THREE.Scene,
        camera: THREE.Camera,
        targetScene?: THREE.Scene | null
      ) => unknown
    }

    const precompileRenderer = renderer as PrecompileRenderer
    if (typeof precompileRenderer.compileAsync === 'function') {
      const compilePromises: Promise<unknown>[] = [
        precompileRenderer.compileAsync(this.scene, this.camera),
      ]
      if (this.segmentScene) {
        compilePromises.push(precompileRenderer.compileAsync(this.segmentScene, this.camera))
      }
      return Promise.all(compilePromises).then(() => undefined)
    }

    if (typeof precompileRenderer.compile === 'function') {
      precompileRenderer.compile(this.scene, this.camera)
      if (this.segmentScene) {
        precompileRenderer.compile(this.segmentScene, this.camera)
      }
    }
  }

  /**
   * 使用一个 instanced quad draw 渲染多个 WorkBuffer copy segments。
   *
   * @param renderer - WebGL 渲染器
   * @param segments - WorkBuffer 目标纹理中的 copy segments
   * @param targetSize - 当前 WorkBuffer render target 尺寸
   */
  renderSegments(
    renderer: THREE.WebGLRenderer,
    segments: readonly QuadRenderSegment[],
    targetSize: number
  ): void {
    if (segments.length === 0) {
      return
    }

    this.ensureSegmentGeometry(segments.length)

    const targetSizeUniform = this.material.uniforms.uTargetSize
    if (targetSizeUniform?.value instanceof THREE.Vector2) {
      targetSizeUniform.value.set(targetSize, targetSize)
    }

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i]
      const base = i * 4
      this.segmentRects[base] = segment.x
      this.segmentRects[base + 1] = segment.y
      this.segmentRects[base + 2] = segment.width
      this.segmentRects[base + 3] = segment.height
    }

    if (this.segmentGeometry && this.segmentRectAttribute && this.segmentScene) {
      this.segmentGeometry.instanceCount = segments.length
      this.segmentRectAttribute.needsUpdate = true
      renderer.render(this.segmentScene, this.camera)
    }
  }

  private ensureSegmentGeometry(requiredCapacity: number): void {
    if (!this.segmentGeometry) {
      this.segmentGeometry = new THREE.InstancedBufferGeometry()
      this.segmentGeometry.setAttribute(
        'position',
        new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 1, -1, 0, -1, 1, 0, 1, 1, 0]), 3)
      )
      this.segmentGeometry.setAttribute(
        'uv',
        new THREE.BufferAttribute(new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), 2)
      )
      this.segmentGeometry.setIndex([0, 1, 2, 2, 1, 3])
      this.segmentGeometry.boundingBox = new THREE.Box3(
        new THREE.Vector3(-1, -1, -1),
        new THREE.Vector3(1, 1, 1)
      )
      this.segmentGeometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), Math.SQRT2)

      this.segmentMesh = new THREE.Mesh(this.segmentGeometry, this.material)
      this.segmentMesh.frustumCulled = false
      this.segmentScene = new THREE.Scene()
      this.segmentScene.add(this.segmentMesh)
    }

    if (requiredCapacity <= this.segmentCapacity) {
      return
    }

    let nextCapacity = Math.max(1, this.segmentCapacity)
    while (nextCapacity < requiredCapacity) {
      nextCapacity *= 2
    }

    this.segmentRects = new Float32Array(nextCapacity * 4)
    this.segmentRectAttribute = new THREE.InstancedBufferAttribute(this.segmentRects, 4)
    this.segmentRectAttribute.setUsage(THREE.DynamicDrawUsage)
    this.segmentGeometry.setAttribute('instanceRect', this.segmentRectAttribute)
    this.segmentCapacity = nextCapacity
  }

  /**
   * 销毁资源
   */
  dispose(): void {
    this.geometry.dispose()
    if (this.segmentGeometry) {
      this.segmentGeometry.dispose()
    }
    this.material.dispose()

    this.scene.remove(this.mesh)
    if (this.segmentScene && this.segmentMesh) {
      this.segmentScene.remove(this.segmentMesh)
    }
  }
}
